import { Divider } from 'antd'
import { FC, useEffect, useMemo, useState } from 'react'
import shallow from 'zustand/shallow'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import PerSpacer from '../../../components/custom_components/Spacer'
import { useLiveBoardOrHistory } from '../../../graphql/talents-season/useTalentsSeasonData'
import { TalentsSeasonStatus, RoleName } from '../../../types'
import { useCultivatingTalentsStore } from '../../cultivatingTalents/CultivatingTalentsStore'
import { TalentsSeasonConfirmationActions } from '../../cultivatingTalents/TalentsSeasonConfirmationActions'
import { TalentsSeasonTable } from '../../cultivatingTalents/TalentsSeasonTable'
import { RankingEditImportExportBox } from './RankingEditImportExportBox'
import { RankingEnabledInfoBox } from './RankingEnabledInfoBox'
import { useUserManagersRolesByCompany } from '../../../graphql/company/hooks/useUserManagerRolesByCompany'

interface ExistingTalentsSeasonProps {
  previousSeasonExist: boolean
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
}

export const TalentsSeasonTabContent: FC<ExistingTalentsSeasonProps> = ({
  previousSeasonExist,
  isHRSettings,
  isAllCompaniesSettings,
  isYourCompanyView,
}) => {
  const [rankingEditingInProgress, setRankedEmployees, setEmployees, talentsSeasonSettings, sortRanking, toggleSortRanking] =
    useCultivatingTalentsStore(
      (state) => [
        state.rankingEditingInProgress,
        state.setRankedEmployees,
        state.setEmployees,
        state.talentsSeasonSettings,
        state.sortRanking,
        state.toggleSortRanking,
      ],
      shallow,
    )

  // in parent component TalentsSeasonTab we check for validity of talentsSeasonSettings data
  // and show LoadingAndError if it is undefined, so talentsSeasonSettings! is ok like this
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const settings = talentsSeasonSettings!
  const { companyId, country, year } = settings

  const { data: userRolesData } = useUserManagersRolesByCompany(companyId)
  const userRoles: RoleName[] =
    userRolesData && userRolesData.findUserManagerRolesByCompany ? userRolesData.findUserManagerRolesByCompany : []
  const isCompanyManager = userRoles.includes(RoleName.CompanyManager)

  const { data, loading, error } = useLiveBoardOrHistory(companyId, country, year)

  const sortedData = useMemo(() => {
    const employees = data && data.liveBoardOrHistory ? [...data.liveBoardOrHistory] : []
    return [...employees].sort((x, y) => x.rank - y.rank)
  }, [data])

  const [disclaimerClosed, setDisclaimerClosed] = useState(false)

  useEffect(() => {
    setRankedEmployees(sortedData)
    setEmployees(sortedData)
  }, [setRankedEmployees, sortedData, setEmployees])

  useEffect(() => {
    if (isAllCompaniesSettings || isHRSettings || isYourCompanyView) {
      if (settings.talentsSeasonStatus === TalentsSeasonStatus.Opened) {
        setDisclaimerClosed(false)
      } else {
        setDisclaimerClosed(true)
      }
    } else {
      if ([TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed].includes(settings.talentsSeasonStatus)) {
        setDisclaimerClosed(false)
      } else {
        setDisclaimerClosed(true)
      }
    }
  }, [isYourCompanyView, isAllCompaniesSettings, isHRSettings, settings.talentsSeasonStatus])

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} showSpinner error={error} hasNoData={!data} />
  }

  return (
    <>
      <RankingEnabledInfoBox
        isHRSettings={isHRSettings}
        isAllCompaniesSettings={isAllCompaniesSettings}
        isYourCompanyView={isYourCompanyView}
        settings={settings}
        infoBoxData={sortedData}
        previousSeasonExist={previousSeasonExist}
        setDisclaimerClosed={setDisclaimerClosed}
      />

      <RankingEditImportExportBox
        isHRSettings={isHRSettings}
        isAllCompaniesSettings={isAllCompaniesSettings}
        isYourCompanyView={isYourCompanyView}
        isCompanyManager={isCompanyManager}
      />

      <PerSpacer m="none" className="fullWidth noShadow" testid="spacer">
        <Divider
          type="horizontal"
          className="fullWidth"
          style={{ margin: '0px', padding: '0px', height: '0.1px', backgroundColor: 'light-gray', boxShadow: 'none' }}
        />
      </PerSpacer>

      <TalentsSeasonTable
        data={sortedData}
        settings={settings}
        isEditingProcess={rankingEditingInProgress}
        isHRSettings={isHRSettings}
        isAllCompaniesSettings={isAllCompaniesSettings}
        isYourCompanyView={isYourCompanyView}
        isCompanyManager={isCompanyManager}
        disclaimerClosed={disclaimerClosed}
        sortRanking={sortRanking}
        toggleSortRanking={toggleSortRanking}
      />

      {!(isHRSettings || isAllCompaniesSettings || isYourCompanyView) &&
        isCompanyManager &&
        settings.talentsSeasonStatus === TalentsSeasonStatus.Opened && <TalentsSeasonConfirmationActions />}
    </>
  )
}
